'use client';
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AgGridReact } from 'ag-grid-react'; // the AG Grid React Component
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { RichSelectModule } from '@ag-grid-enterprise/rich-select';
import { ModuleRegistry } from '@ag-grid-community/core';
import Loader from '../../shared/component/Loader';
import apiServices from '../../services/service';
import { getUser, getUserList } from '../../redux/selector';
import AddBucketForm from '../bucketManagement/AddBucketForm';
import DeleteConfirmationModal from '../../shared/modal/deleteConfirmation';
import { DateFormatter } from '../../shared/controls/formatters';
import { getSetDatefilterParams } from '../../utils/agGridFilters';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  RangeSelectionModule,
  RowGroupingModule,
  RichSelectModule,
]);

const OptionButton = ({ handleClickOpenEditForm, handleClickDelete, paramsValue }) => {
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleConfirmDelete = () => {
    handleClickDelete(paramsValue);
    setShowConfirmation(false);
  };

  return (
    <div className="action-menu">
      <img
        src="/assets/images/edit.png"
        className="action-img1"
        onClick={() => handleClickOpenEditForm(paramsValue)}
        alt="Edit Bucket"
        title="Edit Bucket"
      />
      <img
        src="/assets/images/deleteNotes.png"
        className="action-img2"
        onClick={() => setShowConfirmation(true)}
        alt="Delete Bucket"
        title="Delete Bucket"
      />
      <DeleteConfirmationModal
        show={showConfirmation}
        handleClose={() => setShowConfirmation(false)}
        handleConfirm={handleConfirmDelete}
        content="bucket"
      />
    </div>
  );
};

function BucketList() {
  const gridRef = useRef(); // Optional - for accessing Grid's API
  const [rowData, setRowData] = useState([]);
  const [showAddBucketModal, setShowAddBucketModal] = useState(false);
  const [editingBucket, setEditingBucket] = useState(null);
  const [loading, setLoading] = useState(false);
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);

  const userInfo = useSelector(getUser);
  const userId = userInfo?.user?.userId;
  const usersList = useSelector(getUserList);

  const dateFilterParams = useMemo(() => getSetDatefilterParams(), []);

  const handleClickOpenEditForm = (bucket) => {
    setEditingBucket(bucket);
    setShowAddBucketModal(true);
  };

  const handleClickDelete = (bucket) => {
    setLoading(true);
    apiServices
      .deleteBuckets({ id: bucket.id })
      .then(() => {
        toast.success('Bucket deleted successfully!', { position: 'top-right' });
        refreshBuckets();
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.message || error.message || 'Failed to delete bucket',
          { position: 'top-right' }
        );
      })
      .finally(() => setLoading(false));
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: 'Actions',
        cellRenderer: (params) => (
          <OptionButton
            paramsValue={{ ...params.data }}
            handleClickOpenEditForm={handleClickOpenEditForm}
            handleClickDelete={handleClickDelete}
          />
        ),
        maxWidth: 100,
        minWidth: 100,
        sortable: false,
        filter: false,
        pinned: 'left',
        lockPosition: 'left',
      },
      {
        headerName: 'Bucket Name',
        field: 'name',
        filter: 'agTextColumnFilter',
        filterParams: { buttons: ['apply', 'reset'], closeOnApply: true },
      },
      {
        headerName: 'Access Level',
        field: 'accessLevel',
        filter: 'agTextColumnFilter',
        filterParams: { buttons: ['apply', 'reset'], closeOnApply: true },
      },
      {
        headerName: 'Expiry Date',
        field: 'expiryDateTime',
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
        valueFormatter: DateFormatter,
      },
      {
        headerName: 'Bucket Visibility',
        field: 'bucketVisibility',
        filter: 'agTextColumnFilter',
        filterParams: { buttons: ['apply', 'reset'], closeOnApply: true },
      },
    ],
    [dateFilterParams]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      editable: false,
      flex: 1,
      minWidth: 130,
      filter: true,
      resizable: false,
    }),
    []
  );

  const onFilterTextBoxChanged = useCallback(() => {
    gridRef.current.api.setQuickFilter(document.getElementById('filter-text-box').value);
  }, []);

  const popupParent = useMemo(() => document.body, []);

  const onFirstDataRendered = useCallback((params) => {
    gridRef.current.api.paginationGoToPage();
  }, []);

  const refreshBuckets = () => {
    setLoading(true);
    apiServices
      .getBuckets()
      .then((response) => {
        const raw = response.data;
        const data = Array.isArray(raw) ? raw : raw?.bucketList || raw?.buckets || raw?.data || [];
        setRowData(data);
      })
      .catch((error) => console.error('Error fetching buckets:', error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshBuckets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleBucketAdded = () => {
    setShowAddBucketModal(false);
    setEditingBucket(null);
    refreshBuckets();
  };

  const handleCloseBucketModal = () => {
    setShowAddBucketModal(false);
    setEditingBucket(null);
  };

  return (
    <>
      {loading && <Loader />}
      <div className="inner-page" style={containerStyle}>
        <div className="dash-container">
          <div className="tbl_container">
            <div className="row">
              <div className="col-12 col-sm-12 col-md-12">
                <div className="main-hd">
                  <div className="pagetitle">
                    <img src="../assets/images/role-management-icons/left-arrow.png" alt="" />
                    Bucket List
                  </div>

                  <div className="third-div mobView">
                    <div className="dsk">
                      <input
                        className="form-control"
                        id="filter-text-box"
                        type="Text"
                        placeholder="Search in Buckets..."
                        onInput={onFilterTextBoxChanged}
                      />
                    </div>
                    <button
                      className="add-btn"
                      type="button"
                      onClick={() => {
                        setEditingBucket(null);
                        setShowAddBucketModal(true);
                      }}
                    >
                      + Add Bucket
                    </button>
                  </div>
                </div>
                <div className="mob mb-3">
                  <input
                    className="form-control"
                    id="filter-text-box"
                    type="Text"
                    placeholder="Search in Buckets..."
                    onInput={onFilterTextBoxChanged}
                  />
                </div>
                <div className="cutome_ag_theme">
                  <div className="ag-theme-balham tbl_height" style={{ width: '100%', height: '100%' }}>
                    <AgGridReact
                      className="ag-grid-container"
                      domLayout="fixedHeight"
                      ref={gridRef}
                      rowData={rowData}
                      columnDefs={columnDefs}
                      defaultColDef={defaultColDef}
                      animateRows
                      rowSelection="multiple"
                      enableRangeSelection
                      suppressExcelExport
                      suppressRowClickSelection
                      popupParent={popupParent}
                      onFirstDataRendered={onFirstDataRendered}
                      alwaysShowVerticalScroll
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddBucketForm
        open={showAddBucketModal}
        onClose={handleCloseBucketModal}
        onSave={handleBucketAdded}
        usersList={usersList}
        editData={editingBucket}
      />
    </>
  );
}

export default BucketList;
